import { useEffect, useMemo, useState } from 'react'
import { fetchAnnualOkrs, fetchGoals, fetchMembers, fetchMeta, fetchSettings, fetchWorkLogs, fetchMyRoutineTasks, updateRoutineTask } from '../utils/api'
import { getCurrentUser } from '../utils/auth'
import type { AnnualOKRPayload, GoalItem, MemberProfile, RoutineTask, SettingsData, WorkLog } from '../types/okr'

function Dashboard() {
  const currentUser = useMemo(() => getCurrentUser(), [])
  const [ownerId, setOwnerId] = useState('GS2480')
  const [annualData, setAnnualData] = useState<AnnualOKRPayload>({ year: 2026, okrs: [] })
  const [goals, setGoals] = useState<GoalItem[]>([])
  const [worklogs, setWorklogs] = useState<WorkLog[]>([])
  const [members, setMembers] = useState<MemberProfile[]>([])
  const [memberFilter, setMemberFilter] = useState('')
  const [settings, setSettings] = useState<SettingsData | null>(null)
  const [myRoutines, setMyRoutines] = useState<RoutineTask[]>([])
  const [updatingRoutineId, setUpdatingRoutineId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState<'team' | 'mine'>('team')

  const isOwner = Boolean(currentUser.userId && ownerId && currentUser.userId.toLowerCase() === ownerId.toLowerCase())


  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [annual, goalList, worklogList, routines] = await Promise.all([
          fetchAnnualOkrs(),
          fetchGoals(),
          fetchWorkLogs(),
          fetchMyRoutineTasks()
        ]) as [AnnualOKRPayload, GoalItem[], WorkLog[], RoutineTask[]]
        setAnnualData(annual)
        setGoals(goalList)
        setWorklogs(worklogList)
        setMyRoutines(routines)
        try {
          const settingsData = await fetchSettings()
          setSettings(settingsData)
        } catch {
          setSettings(null)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载仪表盘失败')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const meta = await fetchMeta()
        if (meta?.ownerId) setOwnerId(meta.ownerId)
      } catch {
        // 忽略
      }
    }
    loadMeta()
  }, [])

  useEffect(() => {
    if (!isOwner) {
      setMembers([])
      return
    }
    const loadMembers = async () => {
      try {
        const list = await fetchMembers()
        setMembers(list)
      } catch {
        // 仅 owner 可获取，失败忽略
      }
    }
    loadMembers()
  }, [isOwner])

  // 仪表盘对全员开放，默认展示团队视图

  const isMineLog = (log: WorkLog) => log.ownerId === currentUser.userId || log.ownerName === currentUser.name

  const filteredGoals = useMemo(() => {
    const base = goals
    return memberFilter ? base.filter((g) => g.ownerId === memberFilter || g.owner === memberFilter) : base
  }, [goals, memberFilter])

  const filteredWorklogs = useMemo(() => {
    const base = viewMode === 'mine' ? worklogs.filter(isMineLog) : worklogs
    return memberFilter ? base.filter((log) => log.ownerId === memberFilter || log.ownerName === memberFilter) : base
  }, [worklogs, viewMode, currentUser, memberFilter])

  const filteredAnnualOkrs = useMemo(() => {
    const base = annualData.okrs
    if (!memberFilter) return base
    return base.filter((okr) => okr.ownerId === memberFilter || okr.owner === memberFilter || okr.coOwnerId === memberFilter || okr.coOwner === memberFilter)
  }, [annualData.okrs, memberFilter])

  const okrGoalTree = useMemo(() => {
    const map: Record<string, { monthly: GoalItem[]; weekly: GoalItem[] }> = {}
    goals.forEach((g) => {
      if (!g.relatedOkrId) return
      if (!map[g.relatedOkrId]) map[g.relatedOkrId] = { monthly: [], weekly: [] }
      if (g.level === 'monthly') map[g.relatedOkrId].monthly.push(g)
      if (g.level === 'weekly') map[g.relatedOkrId].weekly.push(g)
    })
    return map
  }, [goals])

  const monthlyGoals = useMemo(() => filteredGoals.filter((goal) => goal.level === 'monthly'), [filteredGoals])
  const weeklyGoals = useMemo(() => filteredGoals.filter((goal) => goal.level === 'weekly'), [filteredGoals])

  const summary = useMemo(() => {
    const blockedGoals = filteredGoals.filter((goal) => goal.status === 'blocked')
    const completedGoals = filteredGoals.filter((goal) => goal.status === 'completed')
    const totalHours = filteredWorklogs.reduce((sum, log) => sum + Number(log.timeSpent || 0), 0)
    const avgProgress = filteredAnnualOkrs.length
      ? Math.round(filteredAnnualOkrs.reduce((sum, okr) => sum + Number(okr.progress || 0), 0) / filteredAnnualOkrs.length)
      : 0

    return {
      monthlyGoals: monthlyGoals.length,
      weeklyGoals: weeklyGoals.length,
      blockedGoals: blockedGoals.length,
      completedGoals: completedGoals.length,
      totalHours,
      avgProgress
    }
  }, [filteredAnnualOkrs, filteredGoals, filteredWorklogs, monthlyGoals.length, weeklyGoals.length])

  const markRoutine = async (id: string, status: 'pending' | 'done', evidence: string) => {
    setUpdatingRoutineId(id)
    try {
      const updated = await updateRoutineTask(id, { status, evidence }) as RoutineTask
      setMyRoutines((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)))
    } catch (err) {
      alert('更新失败，请重试')
    } finally {
      setUpdatingRoutineId('')
    }
  }

  const isAfterFridayCutoff = useMemo(() => {
    const now = new Date()
    const day = now.getDay() // 0 Sun
    const hour = now.getHours()
    return (day === 5 && hour >= 18) || day === 6 || day === 0 // Fri after 18, weekend
  }, [])

  const weeklyReportView = useMemo(() => {
    const summary = settings?.weeklyReport?.summary || '自动汇总周报尚未生成'
    const nextWeekPlans = settings?.weeklyReport?.nextWeekPlans || []
    const risks = settings?.weeklyReport?.risks || []
    if (isAfterFridayCutoff) {
      return {
        title: '下周重点（提前查看）',
        summary: summary,
        plans: nextWeekPlans,
        risks
      }
    }
    return {
      title: '本周结论与下周动作',
      summary,
      plans: nextWeekPlans,
      risks
    }
  }, [settings, isAfterFridayCutoff])

  const formatRoutineStatus = (task: RoutineTask) => {
    if (task.status === 'done' && task.lastCompletedAt) {
      const d = new Date(task.lastCompletedAt)
      if (Number.isNaN(d.getTime())) return '已完成'
      if (task.scheduleType === 'weekly') {
        const firstThursday = new Date(d.getFullYear(), 0, 4)
        const diff = d.getTime() - firstThursday.getTime()
        const week = 1 + Math.round((diff / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7)
        return `W${String(week).padStart(2, '0')} 已完成`
      }
      return `${d.getMonth() + 1}月 已完成`
    }
    return '未完成'
  }

  if (loading) {
    return <div className="page-shell"><div className="empty-state">加载仪表盘中...</div></div>
  }

  if (error) {
    return <div className="page-shell"><div className="empty-state error-text">{error}</div></div>
  }

  return (
    <div className="page-shell">
      <section className="hero-panel">
        <div>
          <span className="section-kicker">年度驾驶舱</span>
          <h2>目标管理仪表盘</h2>
          <p>
            已为你导入年度KPI、3月重点事项、本周工作与下周计划。当前可直接在系统中继续维护。
          </p>
        </div>
        <div className="hero-side-note">
          <div className="segmented-control" role="group" aria-label="视图切换">
            <button type="button" className={viewMode === 'team' ? 'active' : ''} onClick={() => setViewMode('team')}>团队视图</button>
            <button type="button" className={viewMode === 'mine' ? 'active' : ''} onClick={() => setViewMode('mine')}>我的</button>
          </div>
          {members.length > 0 && viewMode === 'team' && (
            <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)} className="select-lite">
              <option value="">全部成员</option>
              {members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
            </select>
          )}
          <div className="metric-chip">年度OKR {filteredAnnualOkrs.length} 项</div>
          <div className="metric-chip">运营目标 {filteredGoals.length} 项</div>
          <div className="metric-chip">工作日志 {filteredWorklogs.length} 条</div>
        </div>
      </section>

      <section className="stats-grid">
        <article className="stat-card-modern">
          <span>平均进展</span>
          <strong>{summary.avgProgress}%</strong>
          <p>{viewMode === 'team' ? '全局进度均值' : '我的责任目标进度'}</p>
        </article>
        <article className="stat-card-modern">
          <span>3月重点任务</span>
          <strong>{summary.monthlyGoals}</strong>
          <p>{viewMode === 'team' ? '团队月度重点' : '我的月度责任项'}</p>
        </article>
        <article className="stat-card-modern">
          <span>下周重点计划</span>
          <strong>{summary.weeklyGoals}</strong>
          <p>{viewMode === 'team' ? '团队周计划' : '我的周计划'}</p>
        </article>
        <article className="stat-card-modern">
          <span>风险阻塞</span>
          <strong>{summary.blockedGoals}</strong>
          <p>{viewMode === 'team' ? '需团队协同解除' : '我需跟进的阻塞'}</p>
        </article>
      </section>

      <section className="content-grid two-columns">
        <div className="card-panel">
          <div className="panel-header">
            <div>
              <span className="section-kicker">年度重点</span>
              <h3>OKR进展总览</h3>
            </div>
          </div>
          <div className="stack-list">
            {filteredAnnualOkrs.map((okr) => {
              const tree = okrGoalTree[okr.id] || { monthly: [], weekly: [] }
              return (
                <div key={okr.id} className="stack-item">
                  <div className="item-head">
                    <div>
                      <strong>{okr.objective}</strong>
                      <p>{okr.category} · 负责人：{okr.owner}{okr.coOwner ? ` · 协同人：${okr.coOwner}` : ''}</p>
                    </div>
                    <span className="progress-badge">{okr.progress}%</span>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${okr.progress}%` }} />
                  </div>
                  <ul className="bullet-list compact">
                    {okr.keyResults.map((kr) => (
                      <li key={kr.id}>{kr.title}</li>
                    ))}
                  </ul>
                  {(tree.monthly.length > 0 || tree.weekly.length > 0) ? (
                    <div className="okr-tree">
                      {tree.monthly.length > 0 && (
                        <div className="okr-branch">
                          <p><strong>月度目标</strong></p>
                          <ul className="bullet-list">
                            {tree.monthly.map((m) => (
                              <li key={m.id}>
                                {m.title}（{m.period}）· {m.progress}% · {m.status}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {tree.weekly.length > 0 && (
                        <div className="okr-branch">
                          <p><strong>周目标</strong></p>
                          <ul className="bullet-list compact">
                            {tree.weekly.map((w) => (
                              <li key={w.id}>{w.title}（{w.period}）· {w.status}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="helper-text">尚未关联月/周目标</div>
                  )}
                </div>
              )
            })}
            {filteredAnnualOkrs.length === 0 && <div className="empty-state">暂无数据</div>}
          </div>
        </div>

        <div className="card-panel">
          <div className="panel-header">
            <div>
              <span className="section-kicker">老板版周报</span>
              <h3>{weeklyReportView.title}</h3>
            </div>
          </div>
          <div className="report-summary-box">
            <p>{weeklyReportView.summary}</p>
            <h4>{isAfterFridayCutoff ? '下周重点（提前预览）' : '下周重点'}</h4>
            <ul className="bullet-list">
              {weeklyReportView.plans.length === 0 && <li>暂无数据</li>}
              {weeklyReportView.plans.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <h4>风险提醒</h4>
            <ul className="bullet-list warning-list">
              {weeklyReportView.risks.length === 0 && <li>暂无风险</li>}
              {weeklyReportView.risks.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="content-grid two-columns">
        <div className="card-panel">
          <div className="panel-header">
            <div>
              <span className="section-kicker">月度进展</span>
              <h3>月度目标进展总览</h3>
            </div>
            <span className="panel-meta">{monthlyGoals.length} 项</span>
          </div>
          <div className="stack-list">
            {monthlyGoals.map((goal) => (
              <div key={goal.id} className="stack-item">
                <div className="item-head">
                  <div>
                    <strong>{goal.title}</strong>
                    <p>{goal.period} · 责任人 {goal.owner || goal.ownerId || '未指派'}</p>
                  </div>
                  <span className="progress-badge">{goal.progress}%</span>
                </div>
                <div className="progress-track"><div className="progress-fill" style={{ width: `${goal.progress}%` }} /></div>
                {goal.nextStep && <p className="helper-text">下一步：{goal.nextStep}</p>}
                {goal.blockers && goal.blockers !== '无' && <p className="error-text">阻塞：{goal.blockers}</p>}
              </div>
            ))}
            {monthlyGoals.length === 0 && <div className="empty-state">暂无月度目标</div>}
          </div>
        </div>

        <div className="card-panel">
          <div className="panel-header">
            <div>
              <span className="section-kicker">周度进展</span>
              <h3>周目标进展总览</h3>
            </div>
            <span className="panel-meta">{weeklyGoals.length} 项</span>
          </div>
          <div className="stack-list">
            {weeklyGoals.map((goal) => (
              <div key={goal.id} className="stack-item">
                <div className="item-head">
                  <div>
                    <strong>{goal.title}</strong>
                    <p>{goal.period} · 责任人 {goal.owner || goal.ownerId || '未指派'}</p>
                  </div>
                  <span className={`status-badge ${goal.status}`}>{goal.status}</span>
                </div>
                {goal.blockers && goal.blockers !== '无' && <p className="error-text">阻塞：{goal.blockers}</p>}
              </div>
            ))}
            {weeklyGoals.length === 0 && <div className="empty-state">暂无周度目标</div>}
          </div>
        </div>
      </section>

      <section className="content-grid one-column">
        <div className="card-panel">
          <div className="panel-header">
            <div>
              <span className="section-kicker">例行提醒</span>
              <h3>周期任务</h3>
            </div>
            <span className="panel-meta">共 {myRoutines.length} 条</span>
          </div>
          <div className="stack-list">
            {myRoutines.map((task) => (
              <div key={task.id} className="stack-item">
                <div className="item-head">
                  <div>
                    <strong>{task.title}</strong>
                    <p>{task.description}</p>
                  </div>
                  <span className={`status-badge ${task.status || 'pending'}`}>{formatRoutineStatus(task)}</span>
                </div>
                <div className="chips-row">
                  <span className="pill">{task.scheduleType || 'weekly'} · {task.cron}</span>
                  <span className="pill">责任人：{task.assigneeName || task.assigneeId || '未指派'}</span>
                </div>
                {task.evidence && <p className="helper-text">佐证：{task.evidence}</p>}
                <div className="inline-actions">
                  <button className="secondary-btn" disabled={updatingRoutineId === task.id} onClick={() => markRoutine(task.id, 'done', task.evidence || '例行完成')}>标记完成</button>
                  <button className="text-btn" disabled={updatingRoutineId === task.id} onClick={() => markRoutine(task.id, 'pending', '')}>标记未完成</button>
                </div>
              </div>
            ))}
            {myRoutines.length === 0 && <div className="empty-state">暂无周期任务</div>}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Dashboard
